import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedCallback } from "use-debounce";
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Menu,
  Checkbox,
  Input,
  IconButton,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { FunnelFilterIcon } from "../../../shared/icons/funnel-filter.icon";
import { getCollectiblesTypes } from "../../services";
import { useStyles } from "./collectibles-filters.styles";
import { Button } from "../../../shared/components/button";
import { FilterChip } from "../filter-chip/filter-chip.component";
import { Close } from "@material-ui/icons";

interface CollectiblesType {
  id: number;
  title: string;
}

interface TypeChips {
  id: number;
  title: string;
}

interface CollectiblesFiltersProps {
  typeChips: TypeChips[];
  setTypeChips: (any) => void;
  setDebouncedKeyWord: (string) => void;
  debouncedKeyWord: string | undefined;
}

export const CollectiblesFilters = ({
  typeChips,
  setTypeChips,
  setDebouncedKeyWord,
}: CollectiblesFiltersProps) => {
  const classes = useStyles();
  const [collectiblesType, setCollectiblesType] = useState<CollectiblesType[]>(
    []
  );

  const [checkedTypes, setCheckedTypes] = useState<{
    [key: number]: boolean;
  }>({});

  const { isFetching } = useQuery({
    queryKey: ["collectibles-type"],
    queryFn: getCollectiblesTypes,
    onSuccess: (response) => setCollectiblesType(response),
    refetchOnWindowFocus: false,
    enabled: true,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (typeChips.length < 1) {
      setCheckedTypes({});
    } else {
      setCheckedTypes(
        typeChips.reduce((obj, type) => {
          obj[type.id] = true;
          return obj;
        }, {})
      );
    }
  }, [typeChips, anchorEl]);

  const handleSave = () => {
    setTypeChips(collectiblesType.filter((type) => checkedTypes[type.id]));
    handleClose();
  };

  const handleChange = (checked: boolean, id: number) => {
    setCheckedTypes((prevState) => ({
      ...prevState,
      [id]: checked,
    }));
  };

  const [keyword, setKeyword] = useState("");

  const debouncedSearch = useDebouncedCallback((value) => {
    setDebouncedKeyWord(value);
  }, 300);

  const handleSearchChange = (event) => {
    const { value } = event.target;
    setKeyword(value);
    debouncedSearch.callback(value);
  };

  const handleClearSearch = () => {
    setDebouncedKeyWord("");
    setKeyword("");
  };

  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        className={classes.container}
      >
        <Grid item xs={8} sm={7}>
          <Button
            disabled={isFetching}
            variant="outlined"
            onClick={handleClick}
            endIcon={<ExpandMoreIcon />}
            startIcon={<FunnelFilterIcon />}
          >
            Filter
          </Button>
          {typeChips.map((typeChip) => (
            <React.Fragment key={typeChip.id}>
              <FilterChip
                className={classes.chip}
                data={typeChip}
                setTypeChips={setTypeChips}
              />
            </React.Fragment>
          ))}
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            className={classes.menu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
          >
            <div className={classes.menuArrow} />
            <FormControl component="fieldset">
              <FormGroup>
                {collectiblesType?.map((type) => {
                  const isChecked = checkedTypes[type.id] || false;
                  return (
                    <FormControlLabel
                      label={type.title}
                      key={type.id}
                      control={
                        <Checkbox
                          className={classes.checkbox}
                          name={type.title}
                          checked={isChecked}
                          onChange={(event) =>
                            handleChange(event.target.checked, type.id)
                          }
                        />
                      }
                    />
                  );
                })}
              </FormGroup>
            </FormControl>
            <Grid container className={classes.buttonsContainer}>
              <Button
                name="cancel"
                data-testid="cancel"
                variant="text"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                name="save"
                data-testid="save"
                variant="contained"
                onClick={handleSave}
              >
                Save
              </Button>
            </Grid>
          </Menu>
        </Grid>
        <Grid item xs={4} sm={5}>
          <Input
            value={keyword}
            className={classes.searchField}
            onChange={handleSearchChange}
            placeholder="Search"
            endAdornment={
              <IconButton onClick={handleClearSearch}>
                <Close />
              </IconButton>
            }
          />
        </Grid>
      </Grid>
    </>
  );
};
